import "./InputPicture.scss";
import Avatar from "core/avatar/Avatar";
import Dropdown from "core/dropdown/Dropdown";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { MAX_FILE_SIZE, POPUP_STATUS } from "constants/constants";
import {
  ERROR_UPLOAD_FILE_SIZE,
  ERROR_UPLOAD_FILE_TYPE,
  ERROR_UPLOAD_MISSING_FILE,
} from "constants/popup-codes";
import { uploadFileHelper, displayFileBrowser } from "utils/helper";
import {
  renderEmptyPictureOptions,
  renderPictureOptions,
} from "utils/pictureHelper";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";

const InputPicture = ({
  className,
  id,
  label,
  memberPictureToReplace,
  readOnly,
  required,
  value,
  createFile,
  uploadProfilePicture,
  setCreatedFile,
  setCode,
  setStatus,
  userId,
  errors,
  createdFile,
  placeholderIcon,
  ...props
}) => {
  // Séparation des états: URL string vs fichier pending
  const [inputUrl, setInputUrl] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [inputClicked, setInputClicked] = useState(false);
  const inputFileRef = useRef(null);
  const avatarRef = useRef(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Ref pour tracker la dernière value traitée et éviter les boucles
  const lastProcessedValueRef = useRef(null);

  useEffect(() => {
    if (isUploading && createdFile) {
      setIsUploading(false);
      setPendingFile(null);
    }
  }, [createdFile, isUploading]);

  const checkEmptyInput = () => !inputUrl && !pendingFile && !previewUrl;

  const setProfilePicture = useCallback(
    (file) => {
      if (file) {
        // The pending state waits for `createdFile` to clear itself. Without an
        // uploader nothing would ever clear it, and the component would stop
        // following its `value` prop: only enter it when an upload will happen.
        if (createFile || (uploadProfilePicture && userId)) {
          setIsUploading(true);
          setPendingFile(file);
        }

        if (props.onChange) {
          props.onChange(file);
        }

        uploadFileHelper({
          file,
          type: "profile-picture",
          uploadQueryFunc: createFile,
          uploadProfilePictureFunc: uploadProfilePicture,
          userId,
        });
      }
    },
    [createFile, uploadProfilePicture, userId, props.onChange]
  );

  const validateInput = useCallback(({ file }) => {
    if (file?.size > MAX_FILE_SIZE) {
      return ERROR_UPLOAD_FILE_SIZE;
    } else if (!file?.type?.startsWith("image/")) {
      return ERROR_UPLOAD_FILE_TYPE;
    } else if (required && !file) {
      return ERROR_UPLOAD_MISSING_FILE;
    }
    return null;
  }, [required]);

  // Gestion du fichier sélectionné - validation et upload
  const handleFileSelect = useCallback(
    (file) => {
      const error = validateInput({ file });
      if (error) {
        setStatus?.(POPUP_STATUS.ERROR);
        setCode?.(error);
        return;
      }
      setProfilePicture(file);
    },
    [validateInput, setStatus, setCode, setProfilePicture]
  );

  // Preview URL pour le fichier en cours d'upload
  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setPreviewUrl(null);
  }, [pendingFile]);

  // Synchronisation de la value prop vers inputUrl (évite les boucles)
  useEffect(() => {
    // Ne pas mettre à jour pendant un upload
    if (isUploading || pendingFile) {
      return;
    }

    let newValue = "";
    if (value) {
      if (typeof value === "object") {
        newValue = value.picture || "";
      } else {
        newValue = value;
      }
    }

    // Éviter les mises à jour si la valeur est identique
    if (newValue === lastProcessedValueRef.current) {
      return;
    }

    lastProcessedValueRef.current = newValue;
    setInputUrl(newValue);
  }, [value, isUploading, pendingFile]);

  // Stabilisation du handler avec useCallback
  const handleClickOutside = useCallback(
    (event) => {
      if (
        avatarRef?.current &&
        !avatarRef?.current?.contains(event?.target)
      ) {
        setInputClicked(false);
      }
    },
    []
  );

  useEffect(() => {
    if (inputClicked) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [inputClicked, handleClickOutside]);

  // Handler pour réinitialiser l'image
  const handleClearPicture = useCallback(() => {
    setInputUrl("");
    setPendingFile(null);
    setPreviewUrl(null);
    lastProcessedValueRef.current = "";
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
    if (setCreatedFile) {
      setCreatedFile({ deleted: true });
    }
    if (props.onChange) {
      props.onChange(null);
    }
  }, [setCreatedFile, props.onChange]);

  const renderDropdownOptions = () => {
    const hasPicture = !!(previewUrl || (inputUrl && inputUrl.trim().length > 0));
    if (!hasPicture) {
      return renderEmptyPictureOptions(
        setInputClicked,
        displayFileBrowser,
        `image-picker-${id}`
      );
    }
    return renderPictureOptions(
      setInputClicked,
      handleClearPicture,
      inputFileRef,
      setCreatedFile,
      props.onChange,
      `image-picker-${id}`
    );
  };
  return (
    <div className={`input-picture ${className}`}>
      {label && (
        <div className="label sourcesanspro-normal-shadow-blue-14px">
          <label
            className={
              props.isValidated && checkEmptyInput()
                ? "label-error"
                : "label-standard"
            }
          >
            <FormattedMessage {...label} /> {required && "*"}{" "}
          </label>
          {props.isValidated && checkEmptyInput() && (
            <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
          )}
        </div>
      )}
      {!readOnly && (
        <input
          type="file"
          className="input-hidden"
          accept="image/*"
          id={`image-picker-${id}`}
          ref={inputFileRef}
          onChange={(event) => {
            const file = event?.target?.files?.[0];
            if (file) {
              handleFileSelect(file);
            }
          }}
        />
      )}
      <div className={label ? "avatar" : ""} ref={avatarRef}>
        <Avatar
          replace={memberPictureToReplace}
          background={previewUrl || inputUrl || ""}
          size={label ? "small" : "medium"}
          id={id}
          placeholderIcon={placeholderIcon}
          isBordered={!readOnly}
          onClick={() => {
            if (checkEmptyInput()) {
              displayFileBrowser({ inputElem: `image-picker-${id}` });
            } else {
              setInputClicked(true);
            }
          }}
        />
        {inputClicked && !readOnly && (
          <Dropdown id={`input-select-picture-dropwdown`} width="fit-content">
            {renderDropdownOptions()}
          </Dropdown>
        )}
      </div>
      {showErrorPopup && (
        <ErrorPopup
          errors={errors}
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          label={label || label?.id}
          errorMessage={props.errorMessage}
          errorValue={props.errorValue}
        />
      )}
    </div>
  );
};

InputPicture.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  label: PropTypes.any,
  memberPictureToReplace: PropTypes.any,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  setProfilePicture: PropTypes.func,
  value: PropTypes.any,
  createFile: PropTypes.func,
  uploadProfilePicture: PropTypes.func,
  setCreatedFile: PropTypes.func,
  setCode: PropTypes.func,
  setStatus: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  createdFile: PropTypes.any,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  isValidated: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorValue: PropTypes.any,
  // Forwarded to Avatar: image shown while empty. Defaults to the photo icon.
  placeholderIcon: PropTypes.string,
};

InputPicture.defaultProps = {
  className: "",
  label: null,
  memberPictureToReplace: "",
  readOnly: false,
  required: false,
  setProfilePicture: null,
  value: null,
};

export default InputPicture;
